import * as React from 'react'
import { useState } from 'react'
import {
    Chip,
    Menu,
    MenuItem,
    Box,
    TableFooter,
    TableRow,
    TableCell,
} from '@mui/material'
import { Link } from 'react-router-dom'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown'

import LoadingDefault from '../../../Components/Elements/Loading/LoadingDefault'
import ItemCounter from '../../../Components/Elements/ItemCounter'
import Pagination from '../../../Components/Elements/Pagination'
import DisplayItemHelper from '../../../util/DisplayItemHelper'
import ActionButtons from './Components/ActionButtons'
import { StyledButton } from '@/Components/Elements/StyledComponents/Buttons'

interface ContentDossierIndexFooterProps {
    listItems: Array<IDisplayField>
    dossiers: Array<any>
    kedo: any
    onChangePage: any
    loadingContent: boolean | null
    pager: any
    aggregations: any
    resultAggregations: any
    setAggregations: any
    extraColumns: any
    selected: Array<any>
    subscriptions: Array<any>
    defDossierId: number
    props: any
    mode: string
    limit: any
    handleEntriesChange: any
    onDeleteSuccess: any
    getSearchFilter: any
    fetchDossierResults: any
    favorites: Array<any>
    contents: any
    showArchived: boolean
}

const ContentDossierIndexFooter: React.FC<ContentDossierIndexFooterProps> = ({
    listItems,
    dossiers,
    kedo,
    onChangePage,
    loadingContent,
    pager,
    aggregations,
    setAggregations,
    resultAggregations,
    extraColumns,
    selected,
    subscriptions,
    defDossierId,
    props,
    mode,
    limit,
    handleEntriesChange,
    onDeleteSuccess,
    getSearchFilter,
    fetchDossierResults,
    favorites,
    contents,
    showArchived,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [activeField, setActiveField] = useState<number | null>(null)

    const selectedDossiers = dossiers.filter((item) =>
        selected.find((selectedId) => selectedId === item.id)
    )
    const archivedDossiers =
        showArchived && selectedDossiers.find((item) => item.archived === true)
    const dearchivedDossiers =
        showArchived && selectedDossiers.find((item) => item.archived === false)

    const hasSubscriptions = subscriptions && subscriptions.length > 0
    const totalColumns = listItems.length + extraColumns + 2 // +2 because of the empty cells at the start

    const getAggregationOptions = (field: IDisplayField) => {
        if (field.def_dossier_link) return ['unique']

        const type = field.def_dossier_def_field?.def_field?.type

        if (['amount', 'time'].includes(type))
            return ['avg', 'sum', 'min', 'max']
        if (type === 'date') return ['min', 'max']
        if (type === 'list') return ['unique']

        return []
    }

    const renderAggregateField = (field: IDisplayField) => {
        const aggrOptions = getAggregationOptions(field)

        if (!aggrOptions.length) return null

        return (
            <StyledButton
                className={'aggregate'}
                dense
                sx={{ border: 'none' }}
                size="small"
                onClick={(e) => {
                    setAnchorEl(e.currentTarget)
                    setActiveField(field)
                }}
            >
                <FontAwesomeIcon icon={faChevronDown} />
            </StyledButton>
        )
    }

    const renderAggregateFieldResult = (listItem) => {
        if (!resultAggregations || !resultAggregations[listItem.id]) {
            return null
        }

        if (listItem && listItem.def_dossier_link) {
            return (
                <div className={'aggr-result'}>
                    {resultAggregations[listItem.id].map(
                        (aggrItem, aggrIndex) => (
                            <Chip
                                key={aggrIndex}
                                size="small"
                                label={`${aggrItem.summary} (${aggrItem.total})`}
                                component={Link}
                                to={`/contentdossier/${aggrItem.id}`}
                                sx={{ mr: 1 }}
                            />
                        )
                    )}
                </div>
            )
        } else if (listItem && listItem.def_dossier_def_field) {
            return (
                <div className={'aggr-result'}>
                    {DisplayItemHelper.isTypeOfField(
                        listItem,
                        DisplayItemHelper.FIELD_DATE_TYPE
                    )
                        ? kedo
                              .utils()
                              .dateFormat(resultAggregations[listItem.id])
                        : null}
                    {DisplayItemHelper.isTypeOfField(
                        listItem,
                        DisplayItemHelper.FIELD_TIME_TYPE
                    ) ||
                    DisplayItemHelper.isTypeOfField(
                        listItem,
                        DisplayItemHelper.FIELD_AMOUNT_TYPE
                    )
                        ? resultAggregations[listItem.id]
                        : null}
                    {DisplayItemHelper.isTypeOfField(
                        listItem,
                        DisplayItemHelper.FIELD_LIST_TYPE
                    )
                        ? listItem.def_dossier_def_field.def_field.def_field_lists
                              .filter((defFieldListItem) =>
                                  resultAggregations[listItem.id].find(
                                      (selItem) =>
                                          selItem.id === defFieldListItem.id
                                  )
                              )
                              .map((defFieldListItem, dIndex) => {
                                  const total =
                                      resultAggregations[listItem.id].find(
                                          (selItem) =>
                                              selItem.id === defFieldListItem.id
                                      )?.total ?? 0

                                  if (!total) return null

                                  return (
                                      <Chip
                                          key={dIndex}
                                          size="small"
                                          label={`${kedo.translateItem(
                                              defFieldListItem,
                                              'def_field_list'
                                          )} (${total})`}
                                          sx={{
                                              mr: 1,
                                              backgroundColor:
                                                  defFieldListItem.color ||
                                                  undefined,
                                          }}
                                      />
                                  )
                              })
                        : null}
                </div>
            )
        }
    }

    if (loadingContent) {
        return (
            <TableFooter>
                <TableRow>
                    <TableCell
                        colSpan={totalColumns}
                        sx={{ textAlign: 'center' }}
                    >
                        <LoadingDefault />
                    </TableCell>
                </TableRow>
            </TableFooter>
        )
    }

    if (!dossiers || dossiers.length <= 0) {
        return <tfoot />
    }

    const isAllowedDd = (credential: string): boolean => {
        return kedo
            .env()
            .isAllowedDefDossier(credential, defDossierId, kedo.user())
    }

    return (
        <>
            <TableFooter>
                <TableRow
                    sx={{
                        backgroundColor: 'action.hover',
                        '& .MuiTableCell-root': { verticalAlign: 'top' },
                    }}
                    className={'tr-aggr'}
                >
                    <TableCell sx={{ width: '3%' }} />
                    <TableCell align="center" />
                    {kedo.isAdminOrEnvironmentAdmin()
                        ? listItems.map((listItem, lIndex) => (
                              <TableCell key={lIndex}>
                                  {renderAggregateField(listItem)}
                                  {renderAggregateFieldResult(listItem)}
                              </TableCell>
                          ))
                        : null}
                    {Array.from({ length: extraColumns - 1 }).map((_, i) => (
                        <TableCell key={i} />
                    ))}
                </TableRow>
                {selected.length > 0 && (
                    <TableRow>
                        <TableCell colSpan={totalColumns}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: selected.length
                                        ? 'space-between'
                                        : 'flex-start',
                                    alignItems: 'center',
                                }}
                            >
                                <ActionButtons
                                    selected={selected}
                                    selectedDossiers={selectedDossiers}
                                    isAllowedDd={isAllowedDd}
                                    hasSubscriptions={hasSubscriptions}
                                    dearchivedDossiers={dearchivedDossiers}
                                    archivedDossiers={archivedDossiers}
                                    kedo={kedo}
                                    props={props}
                                    mode={mode}
                                    dossiers={dossiers}
                                    pager={pager}
                                    limit={limit}
                                    handleEntriesChange={handleEntriesChange}
                                    onDeleteSuccess={onDeleteSuccess}
                                    defDossierId={defDossierId}
                                    getSearchFilter={getSearchFilter}
                                    fetchDossierResults={fetchDossierResults}
                                    subscriptions={subscriptions}
                                    favorites={favorites}
                                    contents={contents}
                                />
                            </Box>
                        </TableCell>
                    </TableRow>
                )}
                <TableRow>
                    <TableCell colSpan={totalColumns}>
                        <Pagination pager={pager} onChangePage={onChangePage} />
                        <ItemCounter pager={pager} />
                    </TableCell>
                </TableRow>
            </TableFooter>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => {
                    setAnchorEl(null)
                    setActiveField(null)
                }}
            >
                <MenuItem
                    onClick={() => {
                        const aggrs = aggregations.filter(
                            (item) => item.id !== activeField?.id
                        )
                        setAggregations(aggrs)
                        setAnchorEl(null)
                    }}
                >
                    —
                </MenuItem>

                {activeField &&
                    getAggregationOptions(activeField).map((option) => (
                        <MenuItem
                            key={option}
                            selected={
                                aggregations.find(
                                    (a) => a.id === activeField.id
                                )?.aggregate === option
                            }
                            onClick={() => {
                                const aggrs = aggregations.filter(
                                    (item) => item.id !== activeField.id
                                )

                                aggrs.push({
                                    id: activeField.id,
                                    aggregate: option,
                                })

                                setAggregations(aggrs)
                                setAnchorEl(null)
                            }}
                        >
                            {kedo.t(option.toUpperCase())}
                        </MenuItem>
                    ))}
            </Menu>
        </>
    )
}

export default ContentDossierIndexFooter
